import {
  Award,
  BarChart3,
  Coins,
  LayoutDashboard,
  LogOut,
  MessageCircleQuestion,
  TableOfContents,
  Users,
} from "lucide-react";
import { BillingPeriodType, ContentFormat, ContentType } from "./types";

export const CookieKeys = {
  TOKEN: "par_admin_token",
  REDIRECT_ROUTE: "par_admin_redirect_route",
  USER_TYPE: "par_admin_user_type",
  ERROR_MESSAGE: "gwa_fe_error_message",
};

export const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export const CONTENT_FORMAT = {
  getFormat: function (val: ContentFormat) {
    switch (val.toLowerCase()) {
      case "video":
        return { label: "Video", value: "video" };
      case "audio":
        return { label: "Audio", value: "audio" };
      default:
        return { label: "Video", value: "video" };
    }
  },
  list: [
    { label: "Video", value: "video" },
    { label: "Audio", value: "audio" },
  ],
};

export const CONTENT_TYPE = {
  getType: function (val: ContentType) {
    switch (val.toLowerCase()) {
      case "movie":
        return { label: "Movie", value: "movie" };
      case "series":
        return { label: "Series", value: "series" };
      case "audio":
        return { label: "Audio", value: "audio" };
      default:
        return { label: "Series", value: "series" };
    }
  },
  list: [
    { label: "Movie", value: "movie" },
    { label: "Series", value: "series" },
    { label: "Audio", value: "audio" },
  ],
};

export const BILLING_PERIOD = {
  getPeriod: function (val: BillingPeriodType) {
    switch (val.toLowerCase()) {
      case "monthly":
        return { label: "Monthly", value: "monthly" };
      case "quarterly":
        return { label: "Quarterly", value: "quarterly" };
      // case "Bi-annually":
      //   return { label: "Bi-annually", value: "bi-annually" };
      case "annually":
        return { label: "Annually", value: "annually" };
      default:
        return { label: "Monthly", value: "monthly" };
    }
  },
  list: [
    { label: "Monthly", value: "monthly" },
    { label: "Quarterly", value: "quarterly" },
    // { label: "Bi-annually", value: "bi-annually" },
    { label: "Annually", value: "annually" },
  ],
};

export const RoutesConst = {
  dashboard: "/",
  subscriptions: "/subscriptions",
  leaderboard: "/leaderboard",

  // changed content category to topics then to faculty 24/10/2024
  topics: "/content/faculty",
  viewTopic: (param?: string) => `/content/faculty/${param ?? ":uuid"}`,

  programmes: "/content/programmes",
  createProgramme: "/content/programmes/create",
  viewProgramme: (param?: string) =>
    `/content/programmes/manage/${param ?? ":uuid"}`,

  // changed content-data to faculty then to topic 24/10/2024
  // changed content faculty to content file 7/4/2026
  file: "/content/files",
  createFile: "/content/files/create",

  quiz: "/quiz",
  createQuiz: "/quiz/create",
  editQuiz: (param?: string) => `/quiz/edit/${param ?? ":uuid"}`,

  badges: "/badges",

  users: "/users",
  viewUserChildren: (param?: string) => `/users/${param ?? ":uuid"}/children`,

  storyStop: "/story-stop",
  createStoryStop: "/story-stop/create",
  editStoryStop: (param?: string) => `/story-stop/edit/${param ?? ":uuid"}`,

  pledge: "/pledge",
  createPledge: "/pledge/create",
  editPledge: (param?: string) => `/pledge/edit/${param ?? ":uuid"}`,

  gameStop: "/game-stop",
  createGameStop: "/game-stop/create",
  editGameStop: (param?: string) => `/game-stop/edit/${param ?? ":uuid"}`,

  // test: "/test",
};

export const sideBarRoutes = [
  {
    path: RoutesConst.dashboard,
    title: "Dashboard",
    icon: LayoutDashboard,
  },
  {
    path: RoutesConst.subscriptions,
    title: "Subscriptions",
    icon: Coins,
  },
  {
    path: RoutesConst.leaderboard,
    title: "Leaderboard",
    icon: BarChart3,
  },
  {
    title: "Content",
    icon: TableOfContents,
    children: [
      // {
      //   title: 'Faculty',
      //   id: 'faculty',
      //   path: RoutesConst.topics,
      // },
      {
        title: "Programmes",
        id: "programmes",
        path: RoutesConst.programmes,
      },
      {
        title: "Files",
        id: "files",
        path: RoutesConst.file,
      },
      {
        title: "Story Stop",
        id: "story-stops",
        path: RoutesConst.storyStop,
      },
      {
        title: "Pledge",
        id: "pledges",
        path: RoutesConst.pledge,
      },
      {
        title: "Game Stop",
        id: "game-stops",
        path: RoutesConst.gameStop,
      },
    ],
  },
  {
    path: RoutesConst.quiz,
    title: "Quiz",
    icon: MessageCircleQuestion,
  },
  {
    path: RoutesConst.badges,
    title: "Badges",
    icon: Award,
  },
  {
    path: RoutesConst.users,
    title: "Users",
    icon: Users,
  },
  {
    name: "logout",
    title: "Logout",
    icon: LogOut,
  },

  // { path: RoutesConst.test, title: "Test", icon: Wrench },
];

export enum ApiMethods {
  GET = "GET",
  POST = "POST",
  PATCH = "PATCH",
  PUT = "PUT",
  DELETE = "DELETE",
}

export const CacheKeys = {
  USERS: "users",
  PARENT_CHILD: "parent_child",
  PROFILE: "profile",
  SUBSCRIPTION_PLANS: "subscription_plans",
  SUBSCRIPTION: "subscription",
  TRANSACTIONS: "transactions",
  DASHBOARD: "dashboard",
  PROGRAMME: "programme",
  CONTENT: "content",
  NEXT_CONTENT: "next_content",
  VIEWING_HISTORY: "viewing_history",
  EXPRESS_ORG_CODE: "express_org_code",
  CONTENT_ANALYTICS_SUMMARY: "content_analytics_summary",
  LEADERBOARD: "leaderboard",
  QUIZ: "quiz",
  BADGE: "badge",
  STORY_STOP: "story-stop",
  PLEDGE: "pledge",
  GAME_STOP: "game-stop",
};

export const Benefits = ["eco_house", "hq", "spotlight", "hub_tv"];
